import { FunctionComponent, memo, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { faSignOutAlt, faSpinner } from "@fortawesome/pro-solid-svg-icons";
import * as sentry from "@sentry/react";
import { ClientSignOut } from "@edgetier/client-components";
import { IconTitle } from "@edgetier/components";
import { useFlagsUtilities } from "@edgetier/feature-flags";
import { AbsolutePaths } from "constants/routes";
import { useMediaCheck } from "hooks-for/responsive-ui/use-media-check";
import { PageNames } from "types-for/routes";

import { IProps } from "./sign-out.types";
import { deleteRefreshToken } from "./sign-out.utilities";
import "./sign-out.scss";

/**
 * Navigation button to log out of the application.
 * @returns Sign out button.
 */
const SignOut: FunctionComponent<IProps> = () => {
    const navigate = useNavigate();
    const { logout: featureFlagsLogout } = useFlagsUtilities();
    const [isLoading, setIsLoading] = useState(false);

    const { isMobile, isTablet } = useMediaCheck();

    /**
     * Sign the user out. This makes a backend request although it can't really wait for the response as even in case of
     * a failure the user has to be signed out.
     */
    const signOut = useCallback(async () => {
        try {
            setIsLoading(true);
            if (process.env.NODE_ENV !== "development") {
                try {
                    await featureFlagsLogout();
                } catch (e) {
                    sentry.captureException(e);
                }
            }
            await deleteRefreshToken();
        } catch (e) {
            sentry.captureException(e);
        } finally {
            navigate(AbsolutePaths[PageNames.Login]());
        }
    }, [navigate, featureFlagsLogout]);

    if (isMobile || isTablet) {
        return (
            <div className="sign-out__mobile-button" onClick={isLoading ? undefined : signOut}>
                <IconTitle title="Sign Out" spin={isLoading} icon={isLoading ? faSpinner : faSignOutAlt} />
            </div>
        );
    }

    return <ClientSignOut isLoading={isLoading} onClick={signOut} />;
};

export default memo(SignOut);
